import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  useTheme, 
  useMediaQuery,
  Paper,
  Fade,
  Zoom,
  keyframes
} from '@mui/material';
import { styled } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Home';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { logEvent } from '../config/analytics';

// Animation keyframes
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
  100% { transform: translateY(0px); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.03); }
  100% { transform: scale(1); }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Paper texture background
const paperTexture = `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise' x='0' y='0'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23noise)' opacity='0.05'/%3E%3C/svg%3E")`;

// Styled components
const AnimatedContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '80vh',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  background: paperTexture,
  backgroundBlendMode: 'overlay',
}));

const FloatingIcon = styled(ErrorOutlineIcon)(({ theme }) => ({
  fontSize: '100px',
  color: theme.palette.primary.light,
  marginBottom: theme.spacing(3),
  animation: `${float} 3s ease-in-out infinite`,
  filter: 'drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.1))',
}));

const AnimatedButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(1, 2),
  borderRadius: '20px',
  animation: `${pulse} 2s ease-in-out infinite`,
  transition: 'all 0.3s ease',
  fontSize: '0.9rem',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 5px 10px rgba(0, 0, 0, 0.08)',
  },
})) as typeof Button;

const BackgroundShape = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '250px',
  height: '250px',
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
  opacity: 0.08,
  zIndex: -1,
  animation: `${rotate} 20s linear infinite`,
}));

const BackgroundShape2 = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '180px',
  height: '180px',
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
  opacity: 0.08,
  zIndex: -1,
  animation: `${rotate} 15s linear infinite reverse`,
  right: '10%',
  bottom: '20%',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  maxWidth: '600px',
  margin: '0 auto',
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: paperTexture,
    opacity: 0.5,
    zIndex: -1,
  },
}));

const NotFoundPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    logEvent('error', {category: '404_page_not_found', path: window.location.pathname});
    const timer = setTimeout(() => setShowContent(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Container>
      <AnimatedContainer>
        <BackgroundShape sx={{ left: '10%', top: '20%' }} />
        <BackgroundShape2 />
        
        <Fade in={showContent} timeout={800}>
          <Box>
            <Zoom in={showContent} timeout={1000}>
              <FloatingIcon />
            </Zoom>
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <Typography 
                variant="h1" 
                component="span" 
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.secondary.light} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  px: 2,
                  py: 0.5,
                  borderRadius: '20px',
                  border: `1px solid ${theme.palette.primary.light}`,
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
                }}
              >
                404
              </Typography>
            </Box>
            
            <Typography 
              variant="h3" 
              component="h2" 
              gutterBottom
              sx={{ 
                fontWeight: 600,
                mb: 2,
                color: 'text.primary',
                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' }
              }}
            >
              We can't find that page
            </Typography>
            
            <StyledPaper elevation={0}>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Looks like we've wandered into uncharted territory! Let's get you back on track.
              </Typography>
            </StyledPaper>
            
            <AnimatedButton
              component={Link}
              to="/"
              variant="contained"
              color="primary"
              size="medium"
              startIcon={<HomeIcon />}
            >
              Back to Homepage
            </AnimatedButton>
          </Box>
        </Fade>
      </AnimatedContainer>
    </Container>
  );
};

export default NotFoundPage; 